<script setup lang="ts">
import { computed } from 'vue'
import { useFleetStore } from '@/stores/fleetStore'
import { useRouteStore } from '@/stores/routeStore'
import { CDN } from '@/models/cdn'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  fleetId: {
    type: Number,
    required: true
  }
})

/**
 * Stores
 */
const fleetStore = useFleetStore()
const routeStore = useRouteStore()

/**
 * Variables / Refs
 */
// Add your reactive variables here

/**
 * Computed
 */
const fleetData = computed(() => fleetStore.findFleetById(props.fleetId)!)

// Translate action name from backend
const translateActionName = (actionName: string) => {
  const key = `command.actionNames.${actionName}`
  const translated = t(key)
  // If translation key doesn't exist, return original
  return translated === key ? actionName : translated
}

const actionDisplayText = computed(() => translateActionName(fleetData.value.currentActionName))

// Check if fleet is on a patrol route (but not if defend icon is already showing)
const isOnPatrolRoute = computed(() => {
  return fleetData.value.routeId &&
    fleetData.value.defendActionSet !== 1 &&
    fleetData.value.currentActionName !== 'Patrol' &&
    fleetData.value.currentActionName !== 'Patrol & Defend'
})

const patrolRouteTooltip = computed(() => {
  if (!fleetData.value.routeId) return t('command.ships.current.patrol.tooltip')
  const route = routeStore.findRouteById(fleetData.value.routeId)
  return route ? t('command.ships.current.patrol.patrolling', { name: route.name }) : t('command.ships.current.patrol.tooltip')
})

/**
 * Methods
 */
// Add your methods here

/**
 * Lifecycle hooks
 */
// Add lifecycle hooks if needed (onMounted, onUnmounted, etc.)
</script>

<template>
  <div>
    {{ actionDisplayText }}
    <span
      v-if="fleetData.defendActionSet === 1 && fleetData.currentActionName !== 'Defend'"
      class="pl-1"
    >
      <GenericIcon type="shield" :tooltip="t('command.ships.current.defend.tooltip')" />
    </span>
    <span
      v-if="isOnPatrolRoute"
      class="pl-1"
    >
      <img :src="CDN.icon32('patrol')" :alt="patrolRouteTooltip" :title="patrolRouteTooltip" class="inline w-5 h-5" />
    </span>
    <span v-if="fleetData.actions.length > 1" class="pl-1">
      {{ fleetData.actions.length > 1 ? '+' + (fleetData.actions.length - 1) : '' }}
    </span>
  </div>
</template>

<style scoped lang="scss">
// Add your component-specific styles here
</style>
